import { useState } from 'react';

import { api, ApiError, type Me } from '../lib/api';
import {
  Alert,
  Badge,
  Card,
  Empty,
  PageHead,
  SecretReveal,
  formatTime,
  useLoader,
} from '../components/ui';

type Tab = 'sso' | 'pools' | 'inbound' | 'privacy';

const TABS: { id: Tab; label: string }[] = [
  { id: 'sso', label: 'Single sign-on' },
  { id: 'pools', label: 'Dedicated IPs' },
  { id: 'inbound', label: 'Inbound mail' },
  { id: 'privacy', label: 'Data requests' },
];

function message(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : fallback;
}

/* ------------------------------------------------------------------ SSO */

function Sso() {
  const { data, error, loading, reload } = useLoader(() => api.sso.get());
  const [form, setForm] = useState({
    email_domain: '',
    issuer: '',
    client_id: '',
    client_secret: '',
    default_role: 'member',
  });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      await api.sso.configure(form);
      reload();
    } catch (err) {
      setFormError(message(err, 'Could not save'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <Alert kind="error">{error}</Alert>}

      {data ? (
        <Card title="Current provider">
          <div className="row" style={{ gap: 10, marginBottom: 10 }}>
            <Badge kind="info">{data.provider}</Badge>
            <Badge kind={data.enforced ? 'ok' : 'muted'}>
              {data.enforced ? 'enforced' : 'optional'}
            </Badge>
            <span className="mono">{data.email_domain}</span>
          </div>
          <div className="hint mono">{data.issuer}</div>
          <div className="hint" style={{ marginTop: 4 }}>
            New employees arrive as <strong>{data.default_role}</strong>.
          </div>

          <div style={{ marginTop: 14 }}>
            <button
              type="button"
              className={data.enforced ? 'btn btn-sm' : 'btn btn-sm btn-primary'}
              onClick={async () => {
                if (
                  !data.enforced &&
                  !window.confirm(
                    `Turn off password sign-in for everyone at ${data.email_domain}? ` +
                      'Anyone without an account at the provider will lose access.',
                  )
                ) {
                  return;
                }
                await api.sso.enforce(!data.enforced);
                reload();
              }}
            >
              {data.enforced ? 'Allow passwords again' : 'Require single sign-on'}
            </button>
            {/* Enforcement is the whole point. Without it, an employee who set a
                password before SSO was switched on keeps a way in that the
                identity provider cannot revoke. */}
            <div className="hint" style={{ marginTop: 6 }}>
              Until this is on, anyone who set a password before you configured SSO can
              still use it — and your provider cannot revoke that.
            </div>
          </div>
        </Card>
      ) : (
        !loading && (
          <Empty>
            No identity provider configured. Everyone signs in with a password.
          </Empty>
        )
      )}

      <Card title={data ? 'Replace provider' : 'Connect a provider'}>
        {formError && <Alert kind="error">{formError}</Alert>}
        <form onSubmit={save} className="stack">
          <label>
            Email domain
            <input
              required
              placeholder="acme.com"
              value={form.email_domain}
              onChange={(e) => setForm({ ...form, email_domain: e.target.value })}
            />
            <span className="hint">Everyone with an address here signs in this way.</span>
          </label>
          <label>
            Issuer URL
            <input
              required
              placeholder="https://acme.okta.com"
              value={form.issuer}
              onChange={(e) => setForm({ ...form, issuer: e.target.value })}
            />
            <span className="hint">
              Checked now, not at first login — a wrong URL fails here instead of
              stranding your whole team on a Monday morning.
            </span>
          </label>
          <label>
            Client ID
            <input
              required
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
            />
          </label>
          <label>
            Client secret
            <input
              required
              type="password"
              value={form.client_secret}
              onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
            />
            <span className="hint">Stored write-only. It is never shown again.</span>
          </label>
          <label>
            New members join as
            <select
              value={form.default_role}
              onChange={(e) => setForm({ ...form, default_role: e.target.value })}
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            <span className="hint">
              Owner is not offered: it would hand billing rights to every employee the
              moment SSO is switched on.
            </span>
          </label>
          <div>
            <button className="btn btn-primary" disabled={busy}>
              {busy ? 'Verifying…' : 'Save provider'}
            </button>
          </div>
        </form>
      </Card>

      <Card title="SAML">
        <p style={{ margin: 0 }}>
          Not supported. SAML needs XML signature verification, whose failure mode is
          authentication bypass — it is deliberately unbuilt rather than half-built.
          OIDC covers Okta, Entra ID, Google Workspace and Auth0.
        </p>
      </Card>
    </>
  );
}

/* ------------------------------------------------------------ IP pools */

function Pools() {
  const { data, error, loading, reload } = useLoader(() => api.ipPools.list());
  const eligibility = useLoader(() => api.ipPools.eligibility());
  const [name, setName] = useState('');
  const [mailType, setMailType] = useState('marketing');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const check = eligibility.data;

  return (
    <>
      {error && <Alert kind="error">{error}</Alert>}

      {check && !check.eligible && (
        <Alert kind="warn">
          <strong>Not eligible yet.</strong> {check.reasons.join(' ')} A dedicated IP
          with no volume behind it sends worse than the shared pool — mailbox providers
          trust an unknown address less than a busy one with a record.
        </Alert>
      )}

      <Card title="Request a pool">
        {formError && <Alert kind="error">{formError}</Alert>}
        <form
          className="row"
          onSubmit={async (event) => {
            event.preventDefault();
            setBusy(true);
            setFormError(null);
            try {
              await api.ipPools.create(name, mailType);
              setName('');
              reload();
            } catch (err) {
              setFormError(message(err, 'Could not create pool'));
            } finally {
              setBusy(false);
            }
          }}
        >
          <input
            aria-label="Subdomain name"
            required
            placeholder="acme-marketing"
            value={name}
            onChange={(event) => setName(event.target.value)}
            style={{ maxWidth: 240 }}
          />
          <select
            aria-label="Mail type"
            value={mailType}
            onChange={(event) => setMailType(event.target.value)}
          >
            <option value="marketing">Marketing</option>
            <option value="transactional">Transactional</option>
          </select>
          <button className="btn btn-primary" disabled={busy || !check?.eligible}>
            {busy ? 'Creating…' : 'Create'}
          </button>
        </form>
        <div className="hint" style={{ marginTop: 8 }}>
          Keep marketing and transactional on separate pools. A campaign that draws
          complaints should not be able to stop a password reset from arriving.
        </div>
      </Card>

      <Card tight title={`Pools (${(data ?? []).length})`}>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Pool</th>
                <th>Type</th>
                <th>Warm-up</th>
                <th style={{ textAlign: 'right' }}>Today</th>
                <th style={{ textAlign: 'right' }}>Domains</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(data ?? []).map((pool) => {
                const used = pool.daily_cap
                  ? Math.min(100, Math.round((pool.sent_today / pool.daily_cap) * 100))
                  : 0;
                return (
                  <tr key={pool.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{pool.name}</div>
                      <div className="hint mono">
                        {pool.ip_addresses.length
                          ? pool.ip_addresses.join(', ')
                          : 'no addresses allocated yet'}
                      </div>
                    </td>
                    <td>
                      <Badge kind={pool.mail_type === 'marketing' ? 'info' : 'muted'}>
                        {pool.mail_type}
                      </Badge>
                    </td>
                    <td>
                      <Badge
                        kind={
                          pool.warm_up_status === 'complete'
                            ? 'ok'
                            : pool.warm_up_status === 'paused'
                              ? 'warn'
                              : 'info'
                        }
                      >
                        {pool.warm_up_status}
                      </Badge>
                      {pool.warm_up_status === 'in_progress' && (
                        <div className="hint">day {pool.warm_up_day} of 28</div>
                      )}
                    </td>
                    <td className="mono" style={{ textAlign: 'right' }}>
                      {pool.sent_today.toLocaleString()} / {pool.daily_cap.toLocaleString()}
                      <div className="hint">{used}% of today's cap</div>
                    </td>
                    <td className="mono" style={{ textAlign: 'right' }}>
                      {pool.domain_count}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {pool.warm_up_status === 'pending' && (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={async () => {
                            await api.ipPools.startWarmUp(pool.id);
                            reload();
                          }}
                        >
                          Start warm-up
                        </button>
                      )}
                      {pool.warm_up_status === 'in_progress' && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={async () => {
                            await api.ipPools.pause(pool.id);
                            reload();
                          }}
                        >
                          Pause
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {(data ?? []).length === 0 && (
          <div className="empty">
            {loading ? 'Loading…' : 'No dedicated pools. You are on the shared IPs.'}
          </div>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------- Inbound */

function Inbound({ sendingDomain }: { sendingDomain: string }) {
  const { data, error, loading, reload } = useLoader(() => api.inbound.list());
  const [form, setForm] = useState({
    address_pattern: '',
    domain_name: sendingDomain,
    target_webhook_url: '',
    forward_attachments: false,
  });
  const [created, setCreated] = useState<{ secret: string; pattern: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  return (
    <>
      {error && <Alert kind="error">{error}</Alert>}

      <Card title="Route an address">
        {formError && <Alert kind="error">{formError}</Alert>}
        <form
          className="stack"
          onSubmit={async (event) => {
            event.preventDefault();
            setBusy(true);
            setFormError(null);
            try {
              const route = await api.inbound.create(form);
              setCreated({ secret: route.signing_secret, pattern: route.address_pattern });
              setForm({ ...form, address_pattern: '', target_webhook_url: '' });
              reload();
            } catch (err) {
              setFormError(message(err, 'Could not create route'));
            } finally {
              setBusy(false);
            }
          }}
        >
          <label>
            Address
            <input
              required
              placeholder="support"
              value={form.address_pattern}
              onChange={(event) =>
                setForm({ ...form, address_pattern: event.target.value })
              }
            />
            <span className="hint">
              Or <code>*</code> to catch everything. An exact address always wins over a
              catch-all, so both can exist.
            </span>
          </label>
          <label>
            Domain
            <input
              required
              value={form.domain_name}
              onChange={(event) => setForm({ ...form, domain_name: event.target.value })}
            />
            <span className="hint">Must be a domain already verified on this account.</span>
          </label>
          <label>
            Send to
            <input
              required
              placeholder="https://your-app.com/hooks/inbound"
              value={form.target_webhook_url}
              onChange={(event) =>
                setForm({ ...form, target_webhook_url: event.target.value })
              }
            />
          </label>
          <label className="row" style={{ gap: 8 }}>
            <input
              type="checkbox"
              role="switch"
              checked={form.forward_attachments}
              onChange={(event) =>
                setForm({ ...form, forward_attachments: event.target.checked })
              }
            />
            Include attachment contents
          </label>
          <div className="hint">
            Off by default. Inbound mail is unsolicited, so the attachments are whatever
            a stranger chose to send. Filenames, types and sizes are always included —
            knowing a 20MB executable arrived is useful, being handed it is not.
          </div>
          <div>
            <button className="btn btn-primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create route'}
            </button>
          </div>
        </form>
      </Card>

      {created && (
        <Card title={`Signing secret for ${created.pattern}`}>
          <SecretReveal label="Signing secret" value={created.secret} />
          <div className="hint" style={{ marginTop: 8 }}>
            Shown once. Verify the <code>X-Mailgiri-Signature</code> header on every
            inbound post with it — an endpoint that skips the check will accept
            anything anyone sends it.
          </div>
        </Card>
      )}

      <Card tight title={`Routes (${(data ?? []).length})`}>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Address</th>
                <th>Sends to</th>
                <th style={{ textAlign: 'right' }}>Received</th>
                <th>Last</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(data ?? []).map((route) => (
                <tr key={route.id}>
                  <td className="mono">
                    {route.address_pattern}@{route.domain_name}
                    {route.forward_attachments && (
                      <div className="hint">forwards attachment contents</div>
                    )}
                  </td>
                  <td className="mono">{route.target_webhook_url}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {route.received_count.toLocaleString()}
                  </td>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                    {formatTime(route.last_received_at)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={async () => {
                        if (
                          !window.confirm(
                            `Stop routing ${route.address_pattern}@${route.domain_name}? ` +
                              'Mail sent there will bounce.',
                          )
                        ) {
                          return;
                        }
                        await api.inbound.remove(route.id);
                        reload();
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(data ?? []).length === 0 && (
          <div className="empty">{loading ? 'Loading…' : 'No inbound routes.'}</div>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------- Privacy */

function Privacy() {
  const [email, setEmail] = useState('');
  const [preview, setPreview] = useState<Record<string, number> | null>(null);
  const [result, setResult] = useState<Awaited<
    ReturnType<typeof api.privacy.erase>
  > | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(action: 'preview' | 'export' | 'erase') {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      if (action === 'preview') {
        setPreview(await api.privacy.preview(email));
      } else if (action === 'export') {
        const data = await api.privacy.exportSubject(email);
        // Opened rather than downloaded: the artifact viewer and several
        // browsers block script-initiated saves, and a blank click is worse
        // than a new tab.
        const blob = new Blob([JSON.stringify(data, null, 2)], {
          type: 'application/json',
        });
        window.open(URL.createObjectURL(blob), '_blank');
      } else {
        setResult(await api.privacy.erase(email));
        setPreview(null);
      }
    } catch (err) {
      setError(message(err, 'Request failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Card title="Look up a person">
        {error && <Alert kind="error">{error}</Alert>}
        <div className="row">
          <input
            aria-label="Email address to look up"
            type="email"
            required
            placeholder="someone@example.com"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setPreview(null);
            }}
            style={{ maxWidth: 320 }}
          />
          <button
            type="button"
            className="btn"
            disabled={busy || !email}
            onClick={() => run('preview')}
          >
            What is held
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || !email}
            onClick={() => run('export')}
          >
            Export
          </button>
        </div>
        <div className="hint" style={{ marginTop: 8 }}>
          Export answers a subject access request. Erase answers a deletion request —
          look first, because it cannot be undone.
        </div>
      </Card>

      {preview && (
        <Card title="Held for this address">
          <div className="table-scroll">
            <table>
              <tbody>
                {Object.entries(preview).map(([key, value]) => (
                  <tr key={key}>
                    <td>{key.replace(/_/g, ' ')}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>
                      {typeof value === 'number' ? value.toLocaleString() : String(value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Alert kind="warn">
            Erasing deletes the contact outright, replaces the address on every message
            and event with a one-way pseudonym, and <strong>keeps</strong> the
            suppression as a hash. That last part is deliberate: forgetting that an
            address complained would mean mailing them again, which is the opposite of
            what they asked for.
          </Alert>

          <button
            type="button"
            className="btn btn-danger"
            disabled={busy}
            onClick={() => {
              if (
                !window.confirm(
                  `Erase everything held for ${email}? This cannot be undone and is recorded in the audit log.`,
                )
              ) {
                return;
              }
              run('erase');
            }}
          >
            {busy ? 'Erasing…' : `Erase ${email}`}
          </button>
        </Card>
      )}

      {result && (
        <Card title="Erased">
          <div className="table-scroll">
            <table>
              <tbody>
                <tr>
                  <td>Contacts deleted</td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {result.contacts_deleted}
                  </td>
                </tr>
                <tr>
                  <td>Messages anonymised</td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {result.messages_anonymised}
                  </td>
                </tr>
                <tr>
                  <td>Events anonymised</td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {result.events_anonymised}
                  </td>
                </tr>
                <tr>
                  <td>Suppression kept</td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {result.suppression_retained ? 'yes, hashed' : 'no'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {result.notes.map((note) => (
            <div key={note} className="hint" style={{ marginTop: 6 }}>
              {note}
            </div>
          ))}
        </Card>
      )}
    </>
  );
}

/* ----------------------------------------------------------------- page */

export function Enterprise({ me }: { me: Me }) {
  const [tab, setTab] = useState<Tab>('sso');

  return (
    <>
      <PageHead
        title="Enterprise"
        subtitle="Single sign-on, dedicated sending IPs, inbound routing, and the data requests you are obliged to answer."
      />

      <div className="row" style={{ marginBottom: 14, gap: 6 }}>
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={tab === item.id ? 'btn btn-sm btn-primary' : 'btn btn-sm'}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'sso' && <Sso />}
      {tab === 'pools' && <Pools />}
      {tab === 'inbound' && <Inbound sendingDomain={me.tenant.sending_domain} />}
      {tab === 'privacy' && <Privacy />}
    </>
  );
}
